import { useCallback, useEffect, useRef, useState } from "react";
import { API_CALLS } from "../../../../../API/api_calls";
import { actions, useAppDispatch, useAppSelector } from "../../../../../redux";
import { MeProps } from "../../../../../redux/slices/me/Me";
import { MODAL_Z_INDEX } from "../../../../../utils";
import { Modal } from "../../../../Common";
import { FlexColumn } from "../../../../Common/Styles";
import { EmptySearchResult } from "./EmptySearchResult";
import { FollowersFollowingProps } from "./FollowersFollowing";
import { FollwerUserInfo } from "./FollwerUserInfo";
import { FollowersLoading } from "./Loading";
import { RemoveFollower } from "./RemoveFollower";
import { useFollowersFollowingEmptyState } from "./useFollowersFollowingEmptyState";
import { useObserver } from "./useObserver";

export const Followers = ({
  filterText,
  searchInputId,
}: FollowersFollowingProps.Content) => {
  const dispatch = useAppDispatch();
  const followerRef = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<MeProps.BasicUserInfo | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const {
    followersUsers,
    getFollowersUsers_API_STATUS,
    searchUsers,
    searchUsers_API_STATUS,
  } = useAppSelector((state) => state.connections);
  const { data: users } = followersUsers;
  const FollowersEmptyState = useFollowersFollowingEmptyState({
    title: "No followers yet.",
    iconType: "Users",
  });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!followerRef.current) return;
      const usersList = filterText ? searchUsers.data : users;
      if (!usersList?.length) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < usersList.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          if (focusedIndex === 0) {
            const searchInput = document.getElementById(searchInputId);
            searchInput && (searchInput as HTMLElement).focus();
            setFocusedIndex(-1);
            break;
          }
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
      }
    },
    [filterText, searchUsers.data, users, searchInputId, focusedIndex]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (focusedIndex >= 0) {
      const element = followerRef.current?.children[
        focusedIndex
      ] as HTMLElement;
      element?.focus();
    }
  }, [focusedIndex]);

  //#region useEffects
  useEffect(() => {
    dispatch(actions.connections.resetFollowersUsers());
    API_CALLS.CONNECTIONS.getFollowersUsers(1);
    //eslint-disable-next-line
  }, []);
  useObserver(
    followerRef,
    () => {
      if (filterText) {
        if (!searchUsers.remainingCount) return;
        if (searchUsers_API_STATUS === "progress") return;
        API_CALLS.CONNECTIONS.searchUsers({
          ...searchUsers,
          query: filterText,
          type: "followers",
        });
      } else {
        if (!followersUsers.remainingCount) return;
        if (getFollowersUsers_API_STATUS === "progress") return;
        API_CALLS.CONNECTIONS.getFollowersUsers(followersUsers.page);
      }
    },
    users.length + searchUsers.data.length
  );
  //#endregion

  if (!users || !users.length) {
    if (getFollowersUsers_API_STATUS === "progress")
      return <FollowersLoading />;
    return <FollowersEmptyState />;
  }
  if (filterText && !searchUsers.data.length) {
    const showLoading =
      searchUsers_API_STATUS === "idle" ||
      searchUsers_API_STATUS === "progress";

    if (showLoading) return <FollowersLoading />;
    return <EmptySearchResult />;
  }

  const usersList = filterText ? searchUsers.data : users;

  return (
    <>
      {modalOpen && (
        <Modal
          type="Pages"
          from="followers-modal"
          setCloseButton={setModalOpen}
          showCloseButton={true}
          modalSize="small"
          closeModalOnClickOutside={modalOpen}
          maxWidth={352}
          minWidth={352}
          closeButtonTabIndex={3}
          zIndex={MODAL_Z_INDEX + 1}
        >
          <RemoveFollower
            showModal={modalOpen}
            user={user}
            onCancelClick={() => setModalOpen(false)}
            onConfirmClick={(id) => {
              API_CALLS.CONNECTIONS.removeAFollower({
                id,
                onSuccess: () => setModalOpen(false),
              });
            }}
          />
        </Modal>
      )}
      <FlexColumn ref={followerRef} $gapInPx={8}>
        {usersList.map((user, index) => (
          <FollwerUserInfo
            key={index + user.id}
            type="followers"
            user={user}
            index={index}
            onHover={setFocusedIndex}
            onClick={(user) => {
              setModalOpen(true);
              setUser(user);
            }}
          />
        ))}
      </FlexColumn>
    </>
  );
};
